import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "antd";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { AppModal } from "@/shared/components/dialog";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import {
  assignPatientClinicianApi,
  removePatientClinicianApi,
  type AssignedClinician,
} from "@/portals/admin/api/adminPatientClinicianApi";
import { adminKeys, cliniciansQuery, patientClinicianQuery } from "@/portals/admin/api/adminQueries";
import type { ClinicianListItem } from "@/portals/clinician/api/types";
import type { ContractError } from "@/portals/clinician/api/contractErrors";
import { formatDate } from "@/features/admin/clinicians/components/formatDate";
import { ClinicianPicker } from "@/features/admin/clinicians/components/ClinicianPicker";
import { clinicianName } from "@/portals/clinician/lib/clinicianName";

export const MESSAGES = {
  none: "No clinician assigned. Items go to the shared queue.",
  removeConfirm: "Items this clinician already holds will stay with them. New items will go to the shared queue.",
  patientNotFound: "Patient not found.",
  clinicianNotFound: "That clinician is no longer available.",
};

const BRAND = "!h-auto !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";
const OUTLINE = "!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-medium !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]";

type Dialog = "assign" | "remove" | null;

export function AssignedClinicianSection({ patientId }: { patientId: string }) {
  const [missingAfterAction, setMissingAfterAction] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [choice, setChoice] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  // Cached per patient: reopening this patient shows the assignment at once, then refreshes it quietly.
  const client = useQueryClient();
  const assignment = useQuery(patientClinicianQuery(patientId));
  const assignmentError = assignment.error as ContractError | null;
  useEffect(() => {
    if (assignment.errorUpdatedAt && assignmentError && assignmentError.code !== "PATIENT_NOT_FOUND") {
      toastError(assignmentError.message ?? "Unable to load the assigned clinician.");
    }
    // Once per failed fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignment.errorUpdatedAt]);
  const clinician: AssignedClinician | null = assignment.data?.clinician ?? null;
  const loading = assignment.isPending;
  const patientMissing = missingAfterAction || (assignment.data === undefined && assignmentError?.code === "PATIENT_NOT_FOUND");
  const setClinician = (next: AssignedClinician | null) =>
    client.setQueryData(adminKeys.patientClinician(patientId), (prev: typeof assignment.data) => ({ ...prev, clinician: next }));

  // Only fetched while the assign dialog is open; shares the Clinicians tab's cached list.
  const clinicianList = useQuery({ ...cliniciansQuery(), enabled: dialog === "assign" });
  const options: ClinicianListItem[] = clinicianList.data ?? [];
  const loadOptions = () => client.invalidateQueries({ queryKey: adminKeys.clinicians });

  const openAssign = () => {
    setChoice(undefined);
    setDialog("assign");
  };

  // Toasts carry no names — only the fixed messages below.
  const handleError = (error: ContractError | null) => {
    if (error?.code === "PATIENT_NOT_FOUND") {
      toastError(MESSAGES.patientNotFound);
      setMissingAfterAction(true);
    } else if (error?.code === "CLINICIAN_NOT_FOUND") {
      toastError(MESSAGES.clinicianNotFound);
      void loadOptions();
    } else {
      toastError(error?.message ?? "Something went wrong.");
    }
  };

  const confirmAssign = async () => {
    if (!choice) return;
    setSaving(true);
    const { data, error } = await assignPatientClinicianApi(patientId, choice);
    setSaving(false);
    if (data) {
      setClinician(data.clinician);
      setDialog(null);
      toastSuccess(clinician ? "Assigned clinician changed." : "Clinician assigned.");
      return;
    }
    handleError(error);
    if (error?.code === "PATIENT_NOT_FOUND") setDialog(null);
  };

  const confirmRemove = async () => {
    setSaving(true);
    const { data, error } = await removePatientClinicianApi(patientId);
    setSaving(false);
    setDialog(null);
    if (data) {
      setClinician(null);
      toastSuccess("Assigned clinician removed.");
      return;
    }
    handleError(error);
  };

  return (
    <section aria-label="Assigned clinician" className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E4002B]/50">
            <Icon icon="lucide:stethoscope" className="h-4 w-4 !text-white" />
          </div>
          <h3 className="text-sm font-extrabold text-[#0A2947]">Assigned clinician</h3>
        </div>
        {!loading && !patientMissing && (
          <div className="flex gap-2">
            {clinician && (
              <Button variant="outline" onClick={() => setDialog("remove")} className={OUTLINE}>
                Remove
              </Button>
            )}
            <Button onClick={openAssign} className={BRAND}>
              {clinician ? "Change" : "Assign"}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4">
        {loading ? (
          <Skeleton active title={false} paragraph={{ rows: 2 }} />
        ) : patientMissing ? (
          <p className="text-sm text-[#9CA3AF]">{MESSAGES.patientNotFound}</p>
        ) : clinician ? (
          <dl className="!m-0 grid gap-3 sm:grid-cols-3">
            {[
              { label: "Name", value: clinicianName(clinician) },
              { label: "Email", value: clinician.email },
              { label: "Assigned", value: formatDate(clinician.assignedAt) },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
                <dt className="text-xs text-[#9CA3AF]">{label}</dt>
                <dd className="!m-0 mt-1 break-words text-sm font-semibold text-[#0A2947]">{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-[#6B7280]">{MESSAGES.none}</p>
        )}
      </div>

      <AppModal
        open={dialog === "assign"}
        onOpenChange={(open) => { if (!open && !saving) setDialog(null); }}
        title={<span className="text-[#0A2947]">{clinician ? "Change assigned clinician" : "Assign a clinician"}</span>}
        description={<span className="text-[#4B5563]">New review items for this patient will go to the clinician you choose.</span>}
        width={480}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={saving} onClick={() => setDialog(null)} className={OUTLINE}>Cancel</Button>
            <Button loading={saving} disabled={!choice} onClick={() => void confirmAssign()} className={BRAND}>
              {clinician ? "Change clinician" : "Assign clinician"}
            </Button>
          </div>
        }
      >
        <ClinicianPicker clinicians={options} value={choice} onChange={setChoice} excludeUserId={clinician?.userId} />
      </AppModal>

      <AppModal
        open={dialog === "remove"}
        onOpenChange={(open) => { if (!open && !saving) setDialog(null); }}
        title={<span className="text-[#0A2947]">Remove assigned clinician?</span>}
        description={<span className="text-[#4B5563]">{MESSAGES.removeConfirm}</span>}
        width={480}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={saving} onClick={() => setDialog(null)} className={OUTLINE}>Cancel</Button>
            <Button loading={saving} onClick={() => void confirmRemove()} className={BRAND}>Remove</Button>
          </div>
        }
      />
    </section>
  );
}
